/**
 * Воронки сутності з `/meta` (`entity.behaviors.pipeline`; pipeline-transition.md, розд. 2): ключі
 * механізму й мінімальний розбір конфігурації. Модуль — у першому екрані (перемикач «Воронка |
 * Список» у списку, маршрут дошки), тож без zod і без логіки переходів (вона — у чанку картки,
 * `card/stage.ts`). Платформа не знає воронок компанії (ADR-0003): ключі, підписи й порядок етапів —
 * лише з метаданих.
 */
import type {
  MetaEntity,
  MetaField,
  PipelineMeta,
  PipelineView,
  StageView,
} from '@clientline/contracts';

/** Ключ поведінки й поля механізму воронки (pipeline-transition.md, розд. 2). */
export const PIPELINE_BEHAVIOR = 'pipeline';
export const STAGE_FIELDS = {
  pipeline: 'pipeline',
  stage: 'stage',
  outcome: 'stage_outcome',
  enteredAt: 'stage_entered_at',
  closedAt: 'closed_at',
  reason: 'close_reason',
  comment: 'close_comment',
} as const;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * Публічна конфігурація воронок сутності з `/meta`. Перевірка форми — мінімальна: зіпсована чи
 * невідома форма — `null`, і ні смужки етапів, ні дошки немає (fail closed).
 */
export function pipelineMeta(entity: MetaEntity): PipelineMeta | null {
  const raw = entity.behaviors?.[PIPELINE_BEHAVIOR];
  if (!isObject(raw) || !Array.isArray(raw.pipelines)) return null;
  const pipelines = raw.pipelines.filter(
    (p): p is PipelineView =>
      isObject(p) && typeof p.key === 'string' && Array.isArray(p.stages) && p.stages.length > 0,
  );
  if (pipelines.length === 0) return null;
  const reasons = raw.closeReasons;
  return {
    pipelines,
    closeReasons:
      isObject(reasons) && typeof reasons.entity === 'string'
        ? {
            entity: reasons.entity,
            // `/meta` уже пройшов схему: тут лише вузький тип конфігурації.
            ...(isObject(reasons.restricted)
              ? { restricted: reasons.restricted as { field: string; permission: string } }
              : {}),
          }
        : null,
  };
}

/** Воронки, які можна відкрити дошкою: без архівних (PIP-04); порожньо — дошки немає. */
export function boardPipelines(entity: MetaEntity): PipelineView[] {
  return (pipelineMeta(entity)?.pipelines ?? []).filter(
    (p) => !p.archived && p.stages.some((s) => s.outcome === 'open'),
  );
}

/** Чи має сутність дошку (KAN-01, «доступна для будь-якої сутності з воронкою»). */
export const hasBoard = (entity: MetaEntity): boolean => boardPipelines(entity).length > 0;

/**
 * Воронка дошки: з адреси, якщо вона є серед доступних; інакше — воронка за замовчуванням, далі
 * перша. Чужий чи архівний ключ в адресі веде на типову, а не на помилку.
 */
export function boardPipeline(entity: MetaEntity, key: string | undefined): PipelineView | null {
  const pipelines = boardPipelines(entity);
  return (
    pipelines.find((p) => p.key === key) ?? pipelines.find((p) => p.default) ?? pipelines[0] ?? null
  );
}

/** Стовпці дошки — відкриті етапи в порядку воронки (KAN-01); закриті — зони й «Показати закриті». */
export const openStages = (pipeline: PipelineView): StageView[] =>
  pipeline.stages.filter((s) => s.outcome === 'open');

export const closedStages = (pipeline: PipelineView): StageView[] =>
  pipeline.stages.filter((s) => s.outcome !== 'open');

/** Поле етапу поведінки воронки: його значення показуємо в тоні етапу (`stageTone`). */
export const isStageField = (field: Pick<MetaField, 'key' | 'behavior'> | undefined): boolean =>
  field?.behavior === PIPELINE_BEHAVIOR && field.key === STAGE_FIELDS.stage;

/**
 * Скільки тонів у ряду `--cl-stage-*` (tokens.md, розд. 2.9): відкриті етапи беруть їх по колу.
 */
export const STAGE_TONE_COUNT = 8;

/** Тон етапу: номер тону ряду `--cl-stage-<n>` (1…8) чи результат закритого етапу. */
export type StageTone = number | 'won' | 'lost';

/** Тон відкритого етапу за його місцем серед відкритих етапів воронки (з 0), по колу. */
export const openStageTone = (index: number): number => (index % STAGE_TONE_COUNT) + 1;

/** Прибраний етап воронки (`retired`): лише підпис і результат для історії й фільтрів. */
export type RetiredStage = PipelineView['retired'][number];

/** Знайдений етап: живий (`live`) чи прибраний (`retired`) — разом з воронкою, де він є. */
export type FoundStage =
  | { pipeline: PipelineView; live: StageView; retired?: undefined }
  | { pipeline: PipelineView; live?: undefined; retired: RetiredStage };

/**
 * Етап за ключем — одне правило пошуку для назви й тону етапу. Воронка запису (`pipelineKey`) —
 * першою, далі решта в порядку `/meta`: значення поза воронкою запису (історія, рядок списку без поля
 * воронки) береться з воронки, де етап є. Спершу живі етапи всіх воронок, далі прибрані (історія
 * переживає етап). Невідомий чи порожній ключ — `undefined`.
 */
export function findStage(
  entity: MetaEntity | undefined,
  stage: unknown,
  pipelineKey?: unknown,
): FoundStage | undefined {
  if (typeof stage !== 'string' || stage === '' || !entity) return undefined;
  const pipelines = pipelineMeta(entity)?.pipelines ?? [];
  const own = pipelines.find((p) => p.key === pipelineKey);
  const ordered = own ? [own, ...pipelines.filter((p) => p !== own)] : pipelines;
  for (const pipeline of ordered) {
    const live = pipeline.stages.find((s) => s.key === stage);
    if (live) return { pipeline, live };
  }
  for (const pipeline of ordered) {
    const retired = Array.isArray(pipeline.retired)
      ? pipeline.retired.find((s) => s.key === stage)
      : undefined;
    if (retired) return { pipeline, retired };
  }
  return undefined;
}

/**
 * Тон живого етапу воронки — єдине правило (tokens.md, розд. 2.9): відкритий — тон за його місцем
 * серед відкритих етапів воронки (`openStages`), по колу з `STAGE_TONE_COUNT`; закритий — статусний
 * тон результату. Ним користуються `stageTone`, смужка етапів і шапки дошки.
 */
export const stageToneIn = (
  pipeline: PipelineView,
  stage: Pick<StageView, 'key' | 'outcome'>,
): StageTone =>
  stage.outcome !== 'open'
    ? stage.outcome
    : openStageTone(openStages(pipeline).findIndex((s) => s.key === stage.key));

/**
 * Тон етапу (рішення власника 27.10: кожен етап — свій колір, скрізь, де є етап). Етап шукає
 * `findStage`, тон живого — `stageToneIn`. Детерміновано за метаданими: той самий етап має той самий
 * тон у смужці, «Деталях», «Стрічці», списку й дошці. Прибраний закритий етап — тон результату,
 * прибраний відкритий чи невідомий ключ — `undefined` (нейтральний вигляд). Платформа не знає етапів
 * компанії (ADR-0003): лише порядок і результат з `/meta`.
 */
export function stageTone(
  entity: MetaEntity | undefined,
  stage: unknown,
  pipelineKey?: unknown,
): StageTone | undefined {
  const found = findStage(entity, stage, pipelineKey);
  if (!found) return undefined;
  if (found.live) return stageToneIn(found.pipeline, found.live);
  return found.retired.outcome === 'open' ? undefined : found.retired.outcome;
}

/** Класи тону етапу (`components.css`): `--stage` і `--stage-soft` для елемента; немає — `''`. */
export const stageToneClass = (tone: StageTone | undefined): string =>
  tone === undefined ? '' : `stage-tone stage-tone--${tone}`;
